import { Suspense, useState, type ComponentProps, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import * as RadixTabs from '@radix-ui/react-tabs';
import { cn, focusRing } from './cn';
import { SkeletonBlock } from './Skeleton';

/** Tabs primitives (Radix) in the console's underline style, for custom layouts. */
export const TabsRoot = RadixTabs.Root;

export function TabsList({ className, ...props }: ComponentProps<typeof RadixTabs.List>) {
  return <RadixTabs.List className={cn('flex gap-1 overflow-x-auto border-b border-line', className)} {...props} />;
}

export function TabsTrigger({ className, onPointerDown, onKeyDown, onBlur, ...props }: ComponentProps<typeof RadixTabs.Trigger>) {
  const [pointerFocus, setPointerFocus] = useState(false);
  return (
    <RadixTabs.Trigger
      className={cn(
        'relative -mb-px m-0 inline-flex h-9 pointer-coarse:h-10 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-t-md border-0 border-b-2 border-transparent bg-transparent px-3 font-sans text-sm font-bold text-ink-muted transition-colors',
        'hover:text-ink data-[state=active]:border-accent data-[state=active]:text-accent',
        focusRing,
        // Keep the keyboard focus ring inside the horizontally scrolling tab list.
        'focus-visible:ring-inset focus-visible:ring-offset-0',
        // Pointer selection uses the active underline; keyboard navigation retains its focus ring.
        pointerFocus && 'focus-visible:ring-0',
        className,
      )}
      {...props}
      onPointerDown={(event) => { setPointerFocus(true); onPointerDown?.(event); }}
      onKeyDown={(event) => { setPointerFocus(false); onKeyDown?.(event); }}
      onBlur={(event) => { setPointerFocus(false); onBlur?.(event); }}
    />
  );
}

export function TabsContent({ className, ...props }: ComponentProps<typeof RadixTabs.Content>) {
  return <RadixTabs.Content className={cn('pt-4 outline-none', className)} {...props} />;
}

export interface TabItem {
  key: string;
  /** Preserve interactive surfaces such as maps while their tab is hidden. */
  keepMounted?: boolean;
  label: ReactNode;
  /** Rendered only while active unless keepMounted is enabled. */
  children: ReactNode;
}

/** Controlled tabs. Use segmented for compact secondary navigation, UrlTabs for page-level tabs. */
export function Tabs({ items, value, onValueChange, className, variant = 'underline' }: { items: TabItem[]; value: string; onValueChange(key: string): void; className?: string; variant?: 'underline' | 'segmented' }) {
  const segmented = variant === 'segmented';
  return (
    <TabsRoot value={value} onValueChange={onValueChange} className={className}>
      <TabsList className={segmented ? 'w-fit max-w-full gap-0.5 rounded-lg border border-line bg-muted p-1 [scrollbar-width:none]' : undefined}>
        {items.map((t) => (
          <TabsTrigger
            key={t.key}
            value={t.key}
            className={segmented ? 'mb-0 h-7 rounded-md border-b-0 px-3 font-medium data-[state=active]:bg-surface data-[state=active]:text-ink data-[state=active]:shadow-sm pointer-coarse:h-9' : undefined}
          >
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {items.map((t) => (
        <TabsContent key={t.key} value={t.key} className={segmented ? 'pt-3' : undefined} forceMount={t.keepMounted ? true : undefined} hidden={value !== t.key}>
          <Suspense fallback={<SkeletonBlock />}>{t.children}</Suspense>
        </TabsContent>
      ))}
    </TabsRoot>
  );
}

/**
 * Tabs whose selection lives in `?tab=` (the first tab has no parameter), so a tab can be linked,
 * bookmarked and survives a reload. Switching replaces the history entry and keeps other query
 * params; an unknown or hidden `?tab=` falls back to the first tab. Hidden tabs unmount, and each
 * loads lazily behind a skeleton.
 */
export function UrlTabs({ items, className }: { items: TabItem[]; className?: string }) {
  const [search, setSearch] = useSearchParams();
  const first = items[0]?.key ?? '';
  const active = items.find((t) => t.key === search.get('tab'))?.key ?? first;

  const select = (key: string) =>
    setSearch(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === first) next.delete('tab');
        else next.set('tab', key);
        return next;
      },
      { replace: true },
    );

  return <Tabs items={items} value={active} onValueChange={select} className={className} />;
}
